import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  timeout: 120000, // 2 minute timeout for large uploads
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('session_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  // Don't set Content-Type for FormData - let browser handle multipart boundary
  if (config.data instanceof FormData) {
    config.headers['Content-Type'] = undefined;
  }
  
  return config;
});

// Handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('session_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  createSession: (sessionId) => api.post('/auth/session', null, {
    headers: { 'X-Session-ID': sessionId }
  }),
  getMe: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
};

export const translatorAPI = {
  createProfile: (data) => api.post('/translators/profile', data),
  getProfile: () => api.get('/translators/profile'),
  listTranslators: () => api.get('/translators'),
  updateCompanyEmail: (translatorId, email) => 
    api.patch(`/translators/${translatorId}/company-email`, { company_email: email }),
};

export const ndaAPI = {
  getTemplate: () => api.get('/nda/template'),
  signNDA: (data) => api.post('/nda/sign', data),
  downloadNDA: (filename) => `${API_URL}/nda/download/${filename}`,
};

export const clientAPI = {
  create: (data) => api.post('/clients', data),
  list: () => api.get('/clients'),
};

export const projectAPI = {
  create: (data) => api.post('/projects', data),
  list: () => api.get('/projects'),
};

export const videoAPI = {
  create: (data) => api.post('/videos', data),
  list: (projectId) => api.get('/videos', { params: { project_id: projectId } }),
};

export const rateAPI = {
  set: (data) => api.post('/rates', data),
  list: (projectId) => api.get('/rates', { params: { project_id: projectId } }),
};

export const assignmentAPI = {
  assign: (data) => api.post('/assignments', data),
  list: (params) => api.get('/assignments', { params }),
  update: (assignmentId, data) => api.patch(`/assignments/${assignmentId}`, data),
};

export const submissionAPI = {
  submit: (month) => api.post('/submissions', { month }),
  list: () => api.get('/submissions'),
  approve: (submissionId) => api.post(`/submissions/${submissionId}/approve`),
  reopen: (submissionId) => api.post(`/submissions/${submissionId}/reopen`),
};

export const invoiceAPI = {
  generate: (data) => api.post('/invoices/generate', data),
  list: (params) => api.get('/invoices', { params }),
  downloadPDF: (invoiceId) => `${API_URL}/invoices/${invoiceId}/pdf`,
  markPaid: (invoiceId, formData) => 
    api.post(`/invoices/${invoiceId}/mark-paid?payment_reference=${formData.get('payment_reference')}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
};

export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
  getReminders: () => api.get('/reminders'),
};

export default api;
